import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import LeadsAssignedService from '../../services/LeadsAssignedService';
import LeadStatusService from '../../services/LeadStatusService';
import AuthService from '../../services/AuthService';

// Fallback color palette for stages that don't have one configured in DB
const COLOR_PALETTE = [
  '#6366f1', '#f59e0b', '#10b981', '#ef4444', '#3b82f6',
  '#8b5cf6', '#fb923c', '#06b6d4', '#00875a', '#e65100',
  '#374151', '#dc2626', '#9ca3af', '#f97316',
];
const bgFor = (color) => color + '18'; // low-opacity version for column bg

function KanbanPipeline() {
  const [allStatuses, setAllStatuses] = useState([]); // loaded from DB
  const [columns, setColumns]         = useState({});
  const [loading, setLoading]         = useState(true);
  const [dragItem, setDragItem]       = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [searchTerm, setSearchTerm]   = useState('');
  const [visibleStatuses, setVisibleStatuses] = useState([]);
  const toast    = useRef(null);
  const navigate = useNavigate();
  const userId = AuthService.getLoggedInUserId();
  const clientId = AuthService.getLoggedInClientId();
  const userRole = parseInt(AuthService.getLoggedInRoleId());
  // Legacy token role id 3 (removed tenant “Manager”) still behaves like Admin for board edits.
  const canEdit  = [1, 2, 3, 4].includes(userRole);

  const buildColumns = useCallback((leads, statuses) => {
    const map = {};
    statuses.forEach(s => { map[s.id] = []; });
    (leads || []).forEach(la => {
      const sid = la.leadStatusId || statuses[0]?.id || 1;
      if (!map[sid]) map[sid] = [];
      map[sid].push(la);
    });
    setColumns(map);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // Load both pipeline stages (from DB) and leads in parallel
        const [statusData, rawLeadData] = await Promise.all([
          LeadStatusService.getPipelineStagesByClientId(clientId),
          LeadsAssignedService.getAllLeadsAssigned(userId, clientId),
        ]);
        // Unwrap ReferenceHandler.Preserve or plain array
        const leadData = Array.isArray(rawLeadData)
          ? rawLeadData
          : (rawLeadData?.$values ?? rawLeadData?.value?.$values ?? []);

        // Normalise statuses — add color/bg from DB field or palette
        const normalised = statusData.map((s, idx) => ({
          id:    s.id,
          name:  s.leadStatusName,
          label: s.leadStatusName,
          color: s.color || COLOR_PALETTE[idx % COLOR_PALETTE.length],
          bg:    bgFor(s.color || COLOR_PALETTE[idx % COLOR_PALETTE.length]),
        }));

        setAllStatuses(normalised);
        setVisibleStatuses(normalised.map(s => s.id));
        buildColumns(leadData, normalised);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, clientId, buildColumns]);

  /* ── Drag & Drop ────────────────────────────────────────────── */
  const handleDragStart = (e, lead, fromStatusId) => {
    setDragItem({ lead, fromStatusId });
    e.dataTransfer.effectAllowed = 'move';
  };

  // Replace a C# DateTime.MinValue ("0001-01-01…") with a safe fallback so SQL
  // Server doesn't reject the datetime column value.
  const safeDate = (v) => {
    if (!v || String(v).startsWith('0001-01-01') || String(v).startsWith('0000')) {
      return new Date().toISOString();
    }
    return v;
  };

  const handleDrop = async (e, toStatusId) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!dragItem || dragItem.fromStatusId === toStatusId) return;
    if (!canEdit) {
      toast.current.show({ severity: 'warn', summary: 'Permission denied', detail: 'You cannot change lead status.' });
      return;
    }

    const { lead, fromStatusId } = dragItem;

    // Optimistic update
    setColumns(prev => {
      const next = { ...prev };
      next[fromStatusId] = (next[fromStatusId] || []).filter(l => l.id !== lead.id);
      next[toStatusId]   = [{ ...lead, leadStatusId: toStatusId }, ...(next[toStatusId] || [])];
      return next;
    });

    const rollback = () => {
      setColumns(prev => {
        const next = { ...prev };
        next[toStatusId]   = (next[toStatusId]   || []).filter(l => l.id !== lead.id);
        next[fromStatusId] = [{ ...lead, leadStatusId: fromStatusId }, ...(next[fromStatusId] || [])];
        return next;
      });
    };

    try {
      // Send only the flat scalar fields the backend DTO needs — omit nested
      // navigation objects (lead, user, leadStatus) to avoid serialisation issues.
      // Sanitise date fields: C# DateTime.MinValue ("0001-01-01") crashes SQL Server.
      const payload = {
        id:                  lead.id,
        leadId:              lead.leadId              || 0,
        leadStatusId:        toStatusId,
        userId:              lead.userId              || 0,
        propertyTypeId:      lead.propertyTypeId      || 0,
        conversationComment: lead.conversationComment || '',
        leadAssignedDate:    safeDate(lead.leadAssignedDate),
        followUpDate:        safeDate(lead.followUpDate),
        nextUpdate:          safeDate(lead.nextUpdate),
        followUpTime:        lead.followUpTime        || '00:00:00',
        leadScore:           lead.leadScore           || 0,
        createdBy:           lead.createdBy           || 0,
        createdOn:           safeDate(lead.createdOn),
        modifiedOn:          new Date().toISOString(),
        modifiedBy:          lead.modifiedBy          || 0,
        isActive:            lead.isActive            ?? true,
      };
      const success = await LeadsAssignedService.updateLeadAssigned(lead.id, payload);
      if (!success) {
        rollback();
        toast.current.show({ severity: 'error', summary: 'Update failed', detail: 'Server did not save the status change. Please try again.' });
      } else {
        toast.current.show({ severity: 'success', summary: 'Status updated', detail: `Moved to ${allStatuses.find(s => s.id === toStatusId)?.label}` });
      }
    } catch (err) {
      rollback();
      toast.current.show({ severity: 'error', summary: 'Update failed', detail: err?.response?.data?.title || err.message || 'Unknown error' });
    }
    setDragItem(null);
  };

  const filteredColumns = useCallback(() => {
    const result = {};
    allStatuses
      .filter(s => visibleStatuses.includes(s.id))
      .forEach(s => {
        const cards = (columns[s.id] || []).filter(la => {
          if (!searchTerm) return true;
          const name  = (la.lead?.firstName || '').toLowerCase();
          const phone = (la.lead?.contactNumber || '').toLowerCase();
          const code  = (la.lead?.leadCode || '').toLowerCase();
          const q     = searchTerm.toLowerCase();
          return name.includes(q) || phone.includes(q) || code.includes(q);
        });
        result[s.id] = { ...s, cards };
      });
    return result;
  }, [columns, visibleStatuses, searchTerm, allStatuses]);

  const toggleStatus = (id) => {
    setVisibleStatuses(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const totalVisible = Object.values(filteredColumns()).reduce((acc, col) => acc + col.cards.length, 0);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner-border text-primary" role="status" />
          <p style={{ marginTop: 12, color: '#8392ab' }}>Loading pipeline…</p>
        </div>
      </div>
    );
  }

  const cols = filteredColumns();

  return (
    <div style={{ padding: '20px 16px', minHeight: '100vh', background: '#f8f9fc' }}>
      <Toast ref={toast} />

      {/* ── Header ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12,
          background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: 20, flexShrink: 0,
        }}>
          <i className="fas fa-columns" />
        </div>
        <div>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#1a1f36' }}>Kanban Pipeline</h5>
          <span style={{ fontSize: 12, color: '#8392ab' }}>{totalVisible} lead{totalVisible !== 1 ? 's' : ''} across {visibleStatuses.length} stages</span>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#8392ab', fontSize: 12 }} />
            <input
              type="text"
              placeholder="Search leads…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: 30, paddingRight: 10, height: 36,
                border: '1.5px solid #e2e8f0', borderRadius: 8,
                fontSize: 13, outline: 'none', width: 200,
                background: '#fff',
              }}
            />
          </div>
          <button
            onClick={() => navigate('/leadassigned')}
            style={{
              height: 36, padding: '0 14px', border: '1.5px solid #e2e8f0',
              borderRadius: 8, background: '#fff', color: '#525f7f',
              fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <i className="fas fa-list" style={{ fontSize: 11 }} /> List View
          </button>
        </div>
      </div>

      {/* ── Stage filter pills ───────────────────────────────── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {allStatuses.map(s => (
          <button
            key={s.id}
            onClick={() => toggleStatus(s.id)}
            style={{
              padding: '3px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
              border: `1.5px solid ${visibleStatuses.includes(s.id) ? s.color : '#e2e8f0'}`,
              background: visibleStatuses.includes(s.id) ? s.bg : '#fff',
              color: visibleStatuses.includes(s.id) ? s.color : '#9ca3af',
              fontWeight: 600, transition: 'all .15s',
            }}
          >
            {s.label}
            {(columns[s.id] || []).length > 0 && (
              <span style={{
                marginLeft: 5, background: visibleStatuses.includes(s.id) ? s.color : '#e2e8f0',
                color: visibleStatuses.includes(s.id) ? '#fff' : '#6b7280',
                borderRadius: 10, padding: '0 5px', fontSize: 10,
              }}>
                {(columns[s.id] || []).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Board ───────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 12, overflowX: 'auto',
        paddingBottom: 16,
        alignItems: 'flex-start',
      }}>
        {Object.values(cols).map(col => (
          <KanbanColumn
            key={col.id}
            col={col}
            isDragOver={dragOverCol === col.id}
            onDragOver={e => { e.preventDefault(); setDragOverCol(col.id); }}
            onDragLeave={() => setDragOverCol(null)}
            onDrop={e => handleDrop(e, col.id)}
            onDragStart={handleDragStart}
            navigate={navigate}
          />
        ))}
      </div>

      {/* Drag hint */}
      {canEdit && (
        <p style={{ textAlign: 'center', color: '#c4c9d4', fontSize: 11, marginTop: 8 }}>
          <i className="fas fa-hand-pointer" style={{ marginRight: 4 }} />
          Drag a card to another column to change its status
        </p>
      )}
    </div>
  );
}

/* ── Column Component ─────────────────────────────────────────── */
function KanbanColumn({ col, isDragOver, onDragOver, onDragLeave, onDrop, onDragStart, navigate }) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{
        minWidth: 230, maxWidth: 250, flexShrink: 0,
        background: isDragOver ? col.bg : '#f1f5f9',
        border: `2px dashed ${isDragOver ? col.color : 'transparent'}`,
        borderRadius: 14,
        transition: 'border .15s, background .15s',
      }}
    >
      {/* Column header */}
      <div style={{
        padding: '10px 12px', borderRadius: '12px 12px 0 0',
        background: col.bg,
        borderBottom: `3px solid ${col.color}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: col.color }}>{col.label}</span>
        <span style={{
          background: col.color, color: '#fff',
          borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700,
        }}>
          {col.cards.length}
        </span>
      </div>

      {/* Cards */}
      <div style={{ padding: '8px 8px', minHeight: 120, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
        {col.cards.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '24px 8px',
            color: '#cbd5e1', fontSize: 12,
          }}>
            <i className="fas fa-inbox" style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
            No leads
          </div>
        ) : (
          col.cards.map(la => (
            <KanbanCard
              key={la.id}
              la={la}
              col={col}
              onDragStart={onDragStart}
              navigate={navigate}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ── Card Component ──────────────────────────────────────────── */
function KanbanCard({ la, col, onDragStart, navigate }) {
  const lead      = la.lead || {};
  const name      = lead.firstName || 'Unknown';
  const phone     = lead.contactNumber || '—';
  const code      = lead.leadCode || '';
  const followUp  = la.nextFollowUpDate
    ? new Date(la.nextFollowUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
    : null;

  const initials = name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, la, la.leadStatusId || 1)}
      onClick={() => navigate(`/editleadassigned/${la.id}`)}
      style={{
        background: '#fff',
        borderRadius: 10,
        padding: '10px 12px',
        marginBottom: 8,
        cursor: 'grab',
        boxShadow: '0 1px 4px rgba(0,0,0,.06)',
        borderLeft: `3px solid ${col.color}`,
        transition: 'box-shadow .15s',
        userSelect: 'none',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.06)'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          background: col.bg, color: col.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 11,
        }}>
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1f36', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </div>
          {code && (
            <span style={{
              background: '#e0e7ff', color: '#4338ca',
              borderRadius: 4, padding: '0 5px', fontSize: 10, fontWeight: 600,
            }}>
              {code}
            </span>
          )}
        </div>
      </div>

      <div style={{ fontSize: 11, color: '#8392ab', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span>
          <i className="fas fa-phone" style={{ marginRight: 5, color: '#10b981', fontSize: 10 }} />
          {phone}
        </span>
        {la.propertyName && (
          <span>
            <i className="fas fa-building" style={{ marginRight: 5, color: '#6366f1', fontSize: 10 }} />
            {la.propertyName}
          </span>
        )}
        {followUp && (
          <span style={{ color: '#f59e0b' }}>
            <i className="fas fa-calendar-check" style={{ marginRight: 5, fontSize: 10 }} />
            Follow-up: {followUp}
          </span>
        )}
      </div>
    </div>
  );
}

export default KanbanPipeline;
